import React, { useEffect } from "react"
import { ValidationRules } from "react-hook-form"
import ImageUploading, { ImageListType } from "react-images-uploading"
import {
  ErrorsContainer,
  ImagePreviewContainer,
  ImageUploadContainer,
  InfoLegend,
} from "~styled/image"
import { ButtonPrimarySoftLarge } from "~styled/button"
import PreviewImage from "~components/imageUpload/PreviewImage"

interface ImageUploadProps {
  name: string
  label: string
  register: (fields: any, rules?: ValidationRules) => void
  setValue: (field: string, val: any) => void
  required?: boolean
  getValues: (field: string) => any
  unregister: (field: string) => void
  maxFileSize?: number
  preview?: boolean
  customErrorMessage?: string
  multiple?: boolean
  maxNumber?: number
  className: string
  acceptTypes?: string[]
  text?: string
  removeIcon?: boolean
}

// Methods to transform file sizes
const megaBytesToBytes = (megaBytes: number) => megaBytes * 1024 * 1024

const ImageUpload = React.memo((props: ImageUploadProps) => {
  // Props and defaults
  const {
    name,
    label,
    register,
    required = false,
    setValue,
    getValues,
    unregister,
    maxFileSize = 1.5,
    customErrorMessage,
    multiple = false,
    maxNumber,
    className,
    acceptTypes,
    text = "Drag a SVG, JPEG or PNG format or upload via the Import button",
    removeIcon = true,
  } = props

  const [images, setImages] = React.useState([])
  const imagesType = acceptTypes ?? ["jpg", "gif", "png", "jpeg", "svg"]
  const maxSize = megaBytesToBytes(maxFileSize)

  useEffect(() => {
    register(name, { required })
    setImages(getValues(name) || [])
    return () => unregister(name)
  }, [name, register, unregister])

  const onChange = (imageList: ImageListType) => {
    if (multiple) {
      setValue(
        name,
        imageList.map(image => image.file)
      )
    } else {
      setValue(name, imageList[0]?.file || "")
    }
    setImages(imageList as never[])
  }

  // Method to handle the prevent default event
  const preventDefault = (event, callback: () => void) => {
    event.preventDefault()
    callback()
  }

  return (
    <ImageUploading
      multiple={multiple}
      value={images}
      onChange={onChange}
      maxNumber={maxNumber}
      acceptType={imagesType}
      maxFileSize={maxSize}
    >
      {({
        onImageUpload,
        onImageRemove,
        dragProps,
        errors,
        onImageRemoveAll,
      }) => (
        <ImageUploadContainer>
          <ImagePreviewContainer className={className} {...dragProps}>
            <PreviewImage
              removeIcon={removeIcon}
              text={text}
              images={images}
              removeImage={onImageRemove}
              imageRemoveAll={onImageRemoveAll}
              className={className}
            />
          </ImagePreviewContainer>

          {/* Button */}
          <div>
            <ButtonPrimarySoftLarge
              style={{ height: 38 }}
              onClick={event => preventDefault(event, onImageUpload)}
              {...dragProps}
            >
              {label}
            </ButtonPrimarySoftLarge>
          </div>

          <InfoLegend>Max file size: {maxFileSize}mb</InfoLegend>
          {/* Errors */}
          {(errors || (!images?.length && customErrorMessage)) && (
            <ErrorsContainer>
              {errors?.maxNumber && (
                <li>Number of selected images exceed {maxNumber}</li>
              )}
              {errors?.acceptType && (
                <li>Your selected file type is not allowed</li>
              )}
              {errors?.maxFileSize && (
                <li>Selected file size exceed {maxFileSize}mb</li>
              )}
              {!images?.length && customErrorMessage && (
                <li>{customErrorMessage}</li>
              )}
            </ErrorsContainer>
          )}
        </ImageUploadContainer>
      )}
    </ImageUploading>
  )
})

export default ImageUpload
